import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Shield, Target, Clock, CheckCircle, Calculator, Phone, ArrowRight, MessageCircle, AlertTriangle, TrendingUp, Heart, BadgeCheck } from "lucide-react";
import { Button } from "@/components/ui/button";

const PHONE = "7410121121";
const WHATSAPP_LINK = `https://wa.me/91${PHONE}?text=Hi,%20I%20want%20to%20discuss%20retirement%20planning%20options`;

const challenges = [
  {
    title: "Underestimating Retirement Years",
    description: "With increasing life expectancy, you may need funds for 25-30 years post-retirement. Most people don't plan for this.",
    icon: Clock
  },
  {
    title: "Inflation Eroding Savings",
    description: "₹1 lakh today will be worth much less in 20 years. Your retirement corpus needs to beat inflation.",
    icon: TrendingUp
  },
  {
    title: "Healthcare Costs Rising",
    description: "Medical expenses increase with age. Many retirees underestimate these costs in their planning.",
    icon: Heart
  },
  {
    title: "Dependency on Children",
    description: "Relying on children for financial support is uncertain. Independent retirement planning ensures dignity.",
    icon: Shield
  }
];

const principles = [
  {
    icon: Clock,
    title: "Start Early",
    description: "The power of compounding works best over long periods. Even small amounts grow significantly over 20-30 years."
  },
  {
    icon: Target,
    title: "Stay Disciplined",
    description: "Monthly systematic saving builds wealth. Avoid the temptation to skip or withdraw prematurely."
  },
  {
    icon: Shield,
    title: "Protect First",
    description: "Ensure adequate life cover before focusing on returns. Your family's security comes first."
  }
];

const RetirementPlanningPage = () => {
  return (
    <div className="min-h-screen bg-white page-transition" data-testid="retirement-planning-page">
      <Helmet>
        <title>Retirement Planning Guide India | LIC Pension Plans | FutureSanchay</title>
        <meta name="description" content="Complete retirement planning guide for Indians. Learn about LIC pension plans, how much corpus you need, and why starting early matters. Free expert consultation." />
        <link rel="canonical" href="https://futuresanchay.com/retirement-planning" />
      </Helmet>
      {/* Hero Header */}
      <section className="pt-8 pb-12 md:pt-12 md:pb-20 relative overflow-hidden" data-testid="retirement-hero">
        {/* Animated background */}
        <div className="absolute inset-0">
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-emerald-50 rounded-full blur-3xl animate-pulse opacity-60"></div>
          <div className="absolute bottom-0 right-1/4 w-80 h-80 bg-sky-50 rounded-full blur-3xl animate-pulse opacity-50" style={{animationDelay: '1s'}}></div>
        </div>

        <div className="container-main relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              {/* Breadcrumb */}
              <div className="flex items-center gap-2 text-sm text-gray-500 mb-6 animate-fade-in-down">
                <Link to="/" className="hover:text-sky-500 transition-colors">Home</Link>
                <span>/</span>
                <span className="text-gray-900 font-medium">Retirement Planning</span>
              </div>

              <div className="inline-flex items-center gap-2 bg-emerald-50 text-emerald-600 px-4 py-2 rounded-full text-sm font-medium mb-4 animate-fade-in-up">
                <Target className="w-4 h-4" />
                Plan for Your Future
              </div>
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-gray-900 mb-6 animate-fade-in-up delay-100">
                Plan for a <span className="text-emerald-500">Dignified Retirement</span>
              </h1>
              <p className="text-lg text-gray-600 mb-8 animate-fade-in-up delay-200">
                Most Indians <span className="font-semibold text-red-500">don't plan adequately</span> for retirement. Don't let that be you. 
                Start early, stay disciplined, and secure your golden years.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 animate-fade-in-up delay-300">
                <Link to="/calculator/retirement">
                  <Button className="h-14 px-8 bg-emerald-500 hover:bg-emerald-600 text-white rounded-xl font-bold shadow-lg hover:shadow-xl transition-all hover:scale-105" data-testid="retirement-calc-btn">
                    <Calculator className="w-5 h-5 mr-2" />
                    Check Your Gap
                  </Button>
                </Link>
                <a href={WHATSAPP_LINK} target="_blank" rel="noopener noreferrer">
                  <Button variant="outline" className="h-14 px-8 rounded-xl font-bold border-2 border-sky-200 text-sky-600 hover:bg-sky-50 hover:border-sky-300 transition-all" data-testid="speak-advisor-btn">
                    <MessageCircle className="w-5 h-5 mr-2" />
                    Speak to Advisor
                  </Button>
                </a>
              </div>
            </div>
            <div className="hidden lg:block animate-fade-in-right">
              <img
                src="https://images.unsplash.com/photo-1529333166437-7750a6dd5a70?w=600&q=85"
                alt="Happy mature couple enjoying quality time"
                className="rounded-3xl shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Why Retirement Planning Fails */}
      <section className="py-16 md:py-24 bg-gray-50" data-testid="challenges-section">
        <div className="container-main">
          <div className="text-center mb-12 animate-fade-in-up">
            <div className="inline-flex items-center gap-2 bg-red-50 text-red-600 px-4 py-2 rounded-full text-sm font-medium mb-4">
              <AlertTriangle className="w-4 h-4" />
              Reality Check
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Why Retirement Planning <span className="text-red-500">Fails</span> in India
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Understanding these challenges is the first step to overcoming them.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {challenges.map((challenge, index) => (
              <div 
                key={index} 
                className="bg-white rounded-2xl border border-gray-100 p-6 shadow-sm hover:shadow-xl transition-all duration-500 hover:-translate-y-2 animate-fade-in-up"
                style={{ animationDelay: `${index * 100}ms` }}
                data-testid={`challenge-${index}`}
              >
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                    <challenge.icon className="w-6 h-6 text-red-500" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">
                      {challenge.title}
                    </h3>
                    <p className="text-gray-600">{challenge.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Principles */}
      <section className="py-16 md:py-24" data-testid="principles-section">
        <div className="container-main">
          <div className="text-center mb-12 animate-fade-in-up">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Principles of <span className="text-sky-500">Disciplined</span> Retirement Planning
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {principles.map((principle, index) => (
              <div 
                key={index} 
                className="text-center animate-bounce-in"
                style={{ animationDelay: `${index * 150}ms` }}
                data-testid={`principle-${index}`}
              >
                <div className="w-20 h-20 bg-sky-100 rounded-3xl flex items-center justify-center mx-auto mb-4 hover:scale-110 hover:rotate-6 transition-all duration-300">
                  <principle.icon className="w-10 h-10 text-sky-500" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  {principle.title}
                </h3>
                <p className="text-gray-600">{principle.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* LIC's Role */}
      <section className="py-16 md:py-24 bg-gray-50" data-testid="lic-role-section">
        <div className="container-main">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="animate-fade-in-up">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                How LIC Fits Into <span className="text-sky-500">Conservative Planning</span>
              </h2>
              <p className="text-gray-600 mb-6">
                For disciplined savers who prioritize capital protection, LIC's traditional plans offer 
                a reliable path to retirement savings:
              </p>
              <ul className="space-y-4">
                {[
                  "Guaranteed life cover throughout the policy term",
                  "Forced savings through regular premium payments",
                  "Tax benefits under Section 80C and 10(10D)",
                  "Bonus additions that grow your corpus over time",
                  "Pension options for regular post-retirement income",
                  "Government backing for enhanced security"
                ].map((point, index) => (
                  <li 
                    key={index} 
                    className="flex items-start gap-3 animate-fade-in-left"
                    style={{ animationDelay: `${index * 100}ms` }}
                  >
                    <div className="w-6 h-6 bg-emerald-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <CheckCircle className="w-4 h-4 text-emerald-500" />
                    </div>
                    <span className="text-gray-700">{point}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-white rounded-3xl border border-gray-100 p-8 shadow-xl animate-fade-in-right">
              <div className="flex items-center gap-2 mb-4">
                <BadgeCheck className="w-6 h-6 text-sky-500" />
                <h3 className="text-2xl font-bold text-gray-900">
                  Our Retirement Planning Approach
                </h3>
              </div>
              <div className="space-y-6">
                {[
                  { step: "1", title: "Assess Your Gap", desc: "Use our calculator to understand your retirement shortfall" },
                  { step: "2", title: "Personalized Recommendations", desc: "Our advisors suggest suitable plans based on your profile" },
                  { step: "3", title: "Ongoing Support", desc: "We help with premium reminders, servicing, and claims" },
                ].map((item, index) => (
                  <div 
                    key={index} 
                    className="flex items-start gap-4 animate-fade-in-up"
                    style={{ animationDelay: `${index * 150}ms` }}
                  >
                    <div className="w-10 h-10 bg-sky-500 text-white rounded-xl flex items-center justify-center font-bold flex-shrink-0 hover:scale-110 transition-transform">
                      {item.step}
                    </div>
                    <div>
                      <h4 className="font-bold text-gray-900">{item.title}</h4>
                      <p className="text-sm text-gray-600">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
              <Link to="/calculator/retirement" className="block mt-6">
                <Button className="w-full h-14 bg-sky-500 hover:bg-sky-600 text-white rounded-xl font-bold shadow-lg hover:shadow-xl transition-all hover:scale-105" data-testid="start-planning-btn">
                  Start Planning
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-20 bg-gradient-to-br from-sky-500 via-sky-600 to-emerald-500 relative overflow-hidden" data-testid="cta-section">
        {/* Animated shapes */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-10 left-10 w-32 h-32 bg-white/10 rounded-full animate-float blur-xl"></div>
          <div className="absolute bottom-10 right-10 w-40 h-40 bg-white/10 rounded-full animate-float blur-xl" style={{animationDelay: '1s'}}></div>
          <div className="absolute top-1/2 left-1/3 w-24 h-24 bg-white/10 rounded-full animate-float blur-xl" style={{animationDelay: '2s'}}></div>
        </div>
        
        <div className="container-main text-center relative z-10">
          <div className="animate-fade-in-up">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Don't Wait Until It's Too Late
            </h2>
            <p className="text-sky-100 max-w-2xl mx-auto mb-8 text-lg">
              Every year you delay retirement planning, you need to save <span className="font-semibold text-white">significantly more</span> to reach the same goal. 
              Start today.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-4 justify-center animate-fade-in-up delay-200">
            <Link to="/calculator/retirement">
              <Button className="h-14 px-8 bg-white text-sky-600 hover:bg-sky-50 rounded-xl font-bold shadow-xl hover:shadow-2xl transition-all hover:scale-110" data-testid="cta-calc-btn">
                <Calculator className="w-5 h-5 mr-2" />
                Calculate Your Gap
              </Button>
            </Link>
            <a href={WHATSAPP_LINK} target="_blank" rel="noopener noreferrer">
              <Button className="h-14 px-8 bg-emerald-500 hover:bg-emerald-600 text-white rounded-xl font-bold shadow-xl hover:shadow-2xl transition-all hover:scale-110 animate-whatsapp-pulse" data-testid="cta-whatsapp-btn">
                <MessageCircle className="w-5 h-5 mr-2" />
                Talk to Advisor
              </Button>
            </a>
          </div>
          <p className="text-xs text-sky-200 mt-6 animate-fade-in-up delay-300">
            * This content is for educational purposes. Consult an advisor for personalized recommendations.
          </p>
        </div>
      </section>
    </div>
  );
};

export default RetirementPlanningPage;
